import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Camera, MapPin, Star, Loader2, CheckCircle, Upload, AlertCircle, Sparkles
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCORING_CATEGORIES = {
  surfaces: { label: "Surfaces", description: "Countertops, tables, shelves" },
  floors: { label: "Floors", description: "All flooring types" },
  appliances: { label: "Appliances", description: "Oven, fridge, microwave" },
  bathroom_buildup: { label: "Bathroom Buildup", description: "Soap scum, hard water, mildew" },
  dust_level: { label: "Dust Level", description: "Visible dust accumulation" },
  kitchen_grease: { label: "Kitchen Grease", description: "Stovetop, hood, backsplash" }
};

const RatingSelector = ({ category, config, value, onChange }) => {
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <div>
          <p className="font-medium text-zinc-900">{config.label}</p>
          <p className="text-xs text-zinc-500">{config.description}</p>
        </div>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((rating) => (
            <button
              key={rating}
              type="button"
              onClick={() => onChange(category, rating)}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                value >= rating
                  ? 'bg-[#F56F05] text-white'
                  : 'bg-zinc-100 text-zinc-400 hover:bg-zinc-200'
              }`}
              data-testid={`after-rating-${category}-${rating}`}
            >
              {rating}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const CleanerReportPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [existingReport, setExistingReport] = useState(null);
  const [beforeAssessment, setBeforeAssessment] = useState(null);
  
  const [ratings, setRatings] = useState({
    surfaces: 4,
    floors: 4,
    appliances: 4,
    bathroom_buildup: 4,
    dust_level: 4,
    kitchen_grease: 4
  });
  
  const [form, setForm] = useState({
    after_overview_photo_url: '',
    after_highlight_photo_url: '',
    notes: ''
  });
  
  const [gpsLocation, setGpsLocation] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const apptRes = await axios.get(`${API_URL}/api/appointments/${jobId}`, { headers });
      setAppointment(apptRes.data);
      
      // Check for before assessment
      try {
        const assessRes = await axios.get(`${API_URL}/api/appointments/${jobId}/condition-assessment`, { headers });
        setBeforeAssessment(assessRes.data);
      } catch (err) {
        if (err?.response?.status !== 404) console.error('Condition-assessment lookup failed:', err);
      }
      
      // Check for existing report
      try {
        const reportRes = await axios.get(`${API_URL}/api/appointments/${jobId}/cleaner-report`, { headers });
        setExistingReport(reportRes.data);
        if (reportRes.data.status === 'fix_requested') {
          setRatings(reportRes.data.ratings);
          setForm({
            after_overview_photo_url: reportRes.data.after_overview_photo_url,
            after_highlight_photo_url: reportRes.data.after_highlight_photo_url,
            notes: reportRes.data.notes || ''
          });
        }
      } catch (err) {
        if (err?.response?.status !== 404) console.error('Existing-report lookup failed:', err);
      }
    } catch (_) {
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [jobId, headers]);

  useEffect(() => {
    fetchData();
    getGPSLocation();
  }, [fetchData]);

  const getGPSLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => { /* GPS unavailable */ }
      );
    }
  };

  const handleRatingChange = (category, value) => {
    setRatings(prev => ({ ...prev, [category]: value }));
  };

  const calculateScore = () => {
    const weights = { surfaces: 0.18, floors: 0.20, appliances: 0.15, bathroom_buildup: 0.17, dust_level: 0.15, kitchen_grease: 0.15 };
    let total = 0;
    for (const [cat, weight] of Object.entries(weights)) {
      const normalized = (ratings[cat] - 1) * 25;
      total += normalized * weight;
    }
    return Math.round(total);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.after_overview_photo_url || !form.after_highlight_photo_url) {
      toast.error('Both photos are required');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/appointments/${jobId}/cleaner-report`, {
        appointment_id: jobId,
        ratings,
        after_overview_photo_url: form.after_overview_photo_url,
        after_highlight_photo_url: form.after_highlight_photo_url,
        notes: form.notes,
        latitude: gpsLocation?.latitude,
        longitude: gpsLocation?.longitude
      }, { headers });
      
      toast.success('Report submitted for QC review');
      navigate(`/cleaner/dashboard`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  const previewScore = calculateScore();
  const beforeScore = beforeAssessment?.before_score;
  const estimatedDelta = beforeScore ? previewScore - beforeScore : null;

  // Check if already submitted and approved
  if (existingReport && existingReport.status !== 'fix_requested') {
    return (
      <div className="min-h-screen bg-zinc-50">
        <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/cleaner/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <h1 className="text-xl font-bold text-zinc-900">After-Clean Report</h1>
          </div>
        </header>
        <div className="max-w-3xl mx-auto px-4 py-6">
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
              <h2 className="text-2xl font-bold text-zinc-900 mb-2">Report Already Submitted</h2>
              <Badge className={existingReport.status === 'released' ? 'bg-green-500' : 'bg-amber-500'}>
                {existingReport.status === 'released' ? 'Approved' : 'Pending QC'}
              </Badge>
              <p className="text-zinc-600 mt-4">
                After Score: <span className="font-bold text-[#F56F05]">{existingReport.after_score}</span>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/cleaner/dashboard')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-zinc-900">After-Clean Report</h1>
            <p className="text-sm text-zinc-500">{appointment?.service?.name} • {appointment?.client_name}</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Fix Requested Banner */}
        {existingReport?.status === 'fix_requested' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800">Revisions Requested</p>
                <p className="text-sm text-amber-700 mt-1">{existingReport.qc_notes}</p>
              </div>
            </div>
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Score Comparison Preview */}
          <Card className="bg-gradient-to-br from-zinc-900 to-zinc-800 text-white overflow-hidden">
            <CardContent className="py-8">
              <div className="grid grid-cols-3 gap-4 text-center">
                {beforeScore && (
                  <div>
                    <p className="text-xs text-zinc-400 mb-1">BEFORE</p>
                    <p className="text-3xl font-bold text-zinc-400">{beforeScore}</p>
                  </div>
                )}
                <div className={beforeScore ? '' : 'col-span-3'}>
                  <p className="text-xs text-zinc-400 mb-1">AFTER (PREVIEW)</p>
                  <p className="text-5xl font-bold text-[#F56F05]" data-testid="preview-after-score">{previewScore}</p>
                  <p className="text-xs text-zinc-400 mt-1">
                    {previewScore <= 40 ? 'Restoration Recommended' :
                     previewScore <= 65 ? 'Improvement In Progress' :
                     previewScore <= 85 ? 'Good Condition' : 'Excellent Condition'}
                  </p>
                </div>
                {estimatedDelta !== null && (
                  <div>
                    <p className="text-xs text-zinc-400 mb-1">IMPROVEMENT</p>
                    <p className={`text-3xl font-bold ${estimatedDelta >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {estimatedDelta >= 0 ? '+' : ''}{estimatedDelta}
                    </p>
                    {estimatedDelta >= 15 && (
                      <Badge className="mt-2 bg-green-500 text-xs">
                        <Sparkles className="h-3 w-3 mr-1" />
                        Great Work!
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Category Ratings */}
          <Card>
            <CardHeader>
              <CardTitle>After-Clean Ratings</CardTitle>
              <CardDescription>Rate each category after cleaning (1 = Poor, 5 = Excellent)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.entries(SCORING_CATEGORIES).map(([category, config]) => (
                <RatingSelector
                  key={category}
                  category={category}
                  config={config}
                  value={ratings[category]}
                  onChange={handleRatingChange}
                />
              ))}
            </CardContent>
          </Card>

          {/* Photos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5" /> Required Photos
              </CardTitle>
              <CardDescription>Document the completed work</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>After Overview Photo *</Label>
                <Input
                  value={form.after_overview_photo_url}
                  onChange={(e) => setForm(prev => ({ ...prev, after_overview_photo_url: e.target.value }))}
                  placeholder="https://... (paste photo URL)"
                  data-testid="after-overview-photo-url"
                />
                <p className="text-xs text-zinc-500 mt-1">Wide shot showing completed clean</p>
              </div>
              <div>
                <Label>After Highlight Photo *</Label>
                <Input
                  value={form.after_highlight_photo_url}
                  onChange={(e) => setForm(prev => ({ ...prev, after_highlight_photo_url: e.target.value }))}
                  placeholder="https://... (paste photo URL)"
                  data-testid="after-highlight-photo-url"
                />
                <p className="text-xs text-zinc-500 mt-1">Close-up of transformed area</p>
              </div>
              
              {gpsLocation && (
                <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                  <MapPin className="h-4 w-4" />
                  GPS Location Captured: {gpsLocation.latitude.toFixed(4)}, {gpsLocation.longitude.toFixed(4)}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any observations, special attention areas, or items for follow-up..."
                rows={3}
                data-testid="cleaner-notes"
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <Button
            type="submit"
            size="lg"
            disabled={submitting}
            className="w-full bg-[#F56F05] hover:bg-[#C2410C]"
            data-testid="submit-report-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 mr-2" />
                Submit Report for QC Review
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CleanerReportPage;
